import algoliasearch from 'algoliasearch'
import algoliarecommend from '@algolia/recommend'
import { createFallbackableCache } from '@algolia/cache-common'
import { createInMemoryCache } from '@algolia/cache-in-memory'
import { createBrowserLocalStorageCache } from '@algolia/cache-browser-local-storage'
import aa from 'search-insights'
import wcs from '../wcs'
import { getCurrentLocale } from './currentCountry'
import i18n, { loadSectionTranslations } from './../../i18n-setup'
import VueCookies from 'vue-cookies'
import { buildNumericFilters, getProductsVariants } from '../wcs/plp'
import { isChanelUnrestrictedCountry } from '@libs/utils/currentCountry'
import { attributesToRetrieveMainProducts, attributesToRetrieveVariants, DEFAULT_PAGE_SIZE, PRODUCTS_PER_ROW } from '@libs/utils/const'
import isEmpty from 'lodash/isEmpty'

const getSafeBrandRuleContext = (query = '', facetFilters = [], isChanelCategory = false) => {
  const isChanel =
    isChanelCategory ||
    query?.toLowerCase().includes('chanel') ||
    facetFilters.some(item => item[0] === 'categories:brands_chanel')
  return isChanel && isChanelUnrestrictedCountry() ? '' : 'SAFEBRANDSEARCH'
}

const buildRuleContexts = (existing, ruleContext) =>
  [...(existing?.split(',') ?? []), ruleContext].filter(Boolean)

let currentLocale = getCurrentLocale().toLowerCase() || 'en-us'
if (currentLocale == 'en-uk') currentLocale = 'en-gb'
let indexEnv =
  window.algoliaConfig?.indexEnv === 'uat_auth'
    ? 'uat_live'
    : window.algoliaConfig?.indexEnv || 'uat_live'
const apiKey = window.algoliaConfig?.apiKey
const appId = window.algoliaConfig?.appId
window.algoliaConfig['isEarlyAccessParam'] = VueCookies.get('earlyAccess')
let queryIDGrouped = []
let objectIDsMapUnGrouped = []
let objectIDsMapPositionGrouped = []
let isSearch = false


export const ALGOLIA_CONFIG = {
  applicationId: appId,
  apiKey: apiKey,
  groupedIndex: `${indexEnv}_sgh_${currentLocale}__grouped`,
  ungroupedIndex: `${indexEnv}_sgh_${currentLocale}__ungrouped`,
  contentIndex: `${indexEnv}_sgh_${currentLocale}__contents`,
}

const caches = [
  createBrowserLocalStorageCache({
    key: `algolia-responses-${ALGOLIA_CONFIG.applicationId}-${1}`,
    timeToLive: 3600, // in seconds (1 hour)
  }),
  createInMemoryCache(),
]

const cacheConfig = {
  requestsCache: createInMemoryCache({ serializable: false }),
  responsesCache: createFallbackableCache({ caches }),
}

export const client = algoliasearch(ALGOLIA_CONFIG.applicationId, ALGOLIA_CONFIG.apiKey, cacheConfig)
const clientRelated = algoliarecommend(ALGOLIA_CONFIG.applicationId, ALGOLIA_CONFIG.apiKey, cacheConfig)
let index = client.initIndex(ALGOLIA_CONFIG.groupedIndex)

const DAY = 1000 * 60 * 60 * 24;

// Initialize Algolia Insights
aa('init', {
  appId: ALGOLIA_CONFIG.applicationId,
  apiKey: ALGOLIA_CONFIG.apiKey,
  useCookie: true,
  cookieDuration: 30 * DAY    // 30 days
});

export const userToken = aa('getUserToken') || VueCookies.get('_ALGOLIA')

export const getPlpPageSize = (hasFilters = false) => {
  if (hasFilters) return DEFAULT_PAGE_SIZE

  const totalBannerSize = [...document.querySelectorAll('[data-plp-banner]')]
    .reduce((sum, el) => sum + (parseInt(el.dataset.bannerSize, 10) || 0), 0)

  const totalElements = DEFAULT_PAGE_SIZE + totalBannerSize
  const remainder = totalElements % PRODUCTS_PER_ROW
  const extraProducts = remainder === 0 ? 0 : PRODUCTS_PER_ROW - remainder

  return DEFAULT_PAGE_SIZE + extraProducts
}

const MAX_MULTI_QUERIES = 50

const QUERY_DEFAULTS = {
  attributesToHighlight: [],
  attributesToSnippet: [],
  explain: false,
  userToken,
  facetingAfterDistinct: true,
}

const FULL_FACETS = ['attributes_translated.*', 'prices.*', 'sort*']

const buildFilterQuery = (indexName, overrides = {}) => ({
  ...QUERY_DEFAULTS,
  indexName,
  clickAnalytics: false,
  hitsPerPage: 0,
  ...overrides,
})

const buildProductQuery = (indexName, overrides = {}) => ({
  ...QUERY_DEFAULTS,
  indexName,
  clickAnalytics: true,
  ...overrides,
})

//grouped search used in plp for first load of products and products update
export const algoliaPlpGroupedSearch = async payload => {
  let { options, addParams } = payload
  let minHinge, maxHinge
  options.facetFilters.forEach((item, index) => {
    if (item[0].includes('HINGE')) {
      const matchValue = item[0].match(/\{([^}]+)\}/)
      const valuesArray = matchValue[1].split(',').map(value => parseInt(value.trim(), 10))
      minHinge = valuesArray[0]
      maxHinge = valuesArray[1]
      const hingeValue = getHingeDistanceArrayValue(minHinge, maxHinge)
      let hingeArray = []
      hingeValue.forEach(value => {
        hingeArray.push(`attributes_translated.HINGE_DISTANCE:${value}`)
      })
      options.facetFilters[index] = hingeArray
    }
  })

  setGroupedIndex(options.orderBy)
  const res = { hits: [], hitsPerPage: 0 }
  const queries = []
  const startPage =
    addParams && typeof addParams.startPage !== 'undefined' ? addParams.startPage : options.page - 1
  // an error on wcs is duplicating params, if this happens we consider only first element of array: https://abstractsrl.atlassian.net/browse/LXSGH21R-8544
  if (Array.isArray(options.hitsPerPage)) {
    options.hitsPerPage = options.hitsPerPage[0]
  }

  const ruleContext = getSafeBrandRuleContext(options.query, options.facetFilters)
  const mergedRuleContexts = buildRuleContexts(options.ruleContexts, ruleContext)

  //use multipleQueries because page can be > 1 and need to retrieve 18 * page products
  for (let page = startPage; page < options.page; page++) {
    queries.push({
      indexName: index.indexName,
      query: options.query,
      params: {
        ...QUERY_DEFAULTS,
        attributesToRetrieve: attributesToRetrieveMainProducts,
        hitsPerPage: options.hitsPerPage,
        facetFilters: options.facetFilters,
        numericFilters: options.numericFilters,
        ruleContexts: mergedRuleContexts,
        page: page,
        clickAnalytics: true,
      }

    })
  }

  //split queries because algolia allows max 50 in multipleQueries api
  const results = []
  while (queries.length > 0) {
    const chunk = queries.splice(0, MAX_MULTI_QUERIES)
    const chunkResults = await client.multipleQueries(chunk)
    results.push(...chunkResults.results)
  }

  //build useful data for pagination and load more component
  const lastEl = results.length - 1
  res.page = results[lastEl].page + 1
  res.hitsPerPage = results[lastEl].hitsPerPage * results.length
  res.nbPages = results[lastEl].nbPages
  res.nbHits = results[lastEl].nbHits
  results.forEach(page => {
    res.hits.push(...page.hits)
  })

  //useful data for analytics
  res.queryID = results[lastEl].queryID
  res.indexName = results[lastEl].index
  res.isSearch = results[lastEl].query !== ''
  //only the last 18 elements
  res.objectIDs = res.hits.slice(-18).map(hit => hit.objectID)
  objectIDsMapPositionGrouped = res.objectIDs.map((hit, index) => ({ index: index + 1, objectID: hit }))
  queryIDGrouped = res.queryID
  isSearch = res.isSearch
  res.renderingContent = results[0].renderingContent
  res.userData = results[0].userData
  res.explain = results[0].explain
  return res
}

//ungrouped search used in plp to retrieve variants
export const algoliaPlpUngroupedSearch = async ({ products, options }) => {
  const { hits } = products
  const ruleContext = getSafeBrandRuleContext(options.query, options.facetFilters)
  const mergedRuleContexts = buildRuleContexts(options.ruleContexts, ruleContext)

  const queries = hits.map(hit =>
    buildProductQuery(getUngroupedIndex(options.orderBy), {
      query: options.query || '',
      attributesToRetrieve: attributesToRetrieveVariants,
      hitsPerPage: 25,
      facetFilters: options.facetFilters.concat([`x_groupkey: ${hit.x_groupkey}`]),
      numericFilters: options.numericFilters,
      ruleContexts: mergedRuleContexts,
    })
  )

  //split queries because algolia allows max 50 in multipleQueries api
  const results = []
  while (queries.length > 0) {
    const chunk = queries.splice(0, MAX_MULTI_QUERIES)
    const chunkResults = await client.multipleQueries(chunk)
    results.push(...chunkResults.results)
  }

  //useful data for analytics
  const objectIDs = results.flatMap(result =>
    result.hits.map((hit, index) => ({
      queryID: result.queryID,
      objectID: hit.objectID,
      position: index + 1,
    }))
  )

  objectIDsMapUnGrouped = [...objectIDsMapUnGrouped, ...objectIDs]

  return results
}

//used to merge main products in grid with all related color variants
export const mergeProdsAndVariants = (payload, forceFirstVariant = true) => {
  const { products, variants } = payload

  products.hits.forEach((productHit, index) => {
    variants.forEach(variant => {
      const variantHits = variant.hits.filter(
        variantHit => variantHit.x_groupkey === productHit.x_groupkey
      )
      if (variantHits.length > 0) {
        if (forceFirstVariant) {
          products.hits[index] = variantHits[0] || productHit
        } else {
          products.hits[index] = productHit
        }
        products.hits[index].colors = [...variantHits]
      }
    })
  })
  return products
}

function sortOptions(facetObject, params) {
  if (!facetObject || typeof facetObject !== 'object') {
    console.warn('sortOptions: facetObject is undefined, null, or not an object')
    return {}
  }

  //return only value present in params.order if params?.sortRemainingBy  equals hidden
  if (params?.sortRemainingBy === 'hidden') {
    const filteredObject = {}

    for (const orderValue of params.order) {
      const upperCaseOrderValue = orderValue.toUpperCase()

      for (const facetKey in facetObject) {
        if (
          facetObject.hasOwnProperty(facetKey) &&
          facetKey.toUpperCase() === upperCaseOrderValue
        ) {
          filteredObject[facetKey] = facetObject[facetKey]
        }
      }
    }

    return filteredObject
  }

  // facetObject = facets - params = renderingContent.facetOrdering.values
  if (params?.order) {
    // Create an array to store the items with their corresponding order value
    const orderingArray = params.order
    let orderArray = []
    const valueNotInOrderingArray = []
    // Extract the items and their counts from the input object
    for (const [key, value] of Object.entries(facetObject)) {
      // If the item contains multiple names separated by '|', take the first one for ordering
      const itemName = key.split('|')[0]
      const orderValue =
        orderingArray.indexOf(itemName) !== -1
          ? orderingArray.indexOf(itemName)
          : orderingArray.length

      // Add the item to orderArray only if it's present in orderingArray
      if (orderValue !== orderingArray.length) {
        orderArray.push({ key, orderValue, value })
      } else {
        valueNotInOrderingArray.push({ key, value })
      }
    }
    const sortedValue = Object.fromEntries(
      // sort all the options by alpha or count
      Object.entries(valueNotInOrderingArray).sort((a, b) => {
        return params?.sortRemainingBy === 'alpha'
          ? a[0].split('|')[0].localeCompare(b[0]) // .split('|')[0] to get translated options
          : b[1] - a[1]
      })
    )
    const sortedValueArrat = Object.values(sortedValue)

    // Sort the items based on the order value
    orderArray.sort((a, b) => a.orderValue - b.orderValue)
    sortedValueArrat.forEach(item => {
      orderArray.push(item)
    })
    // Create a new object with the sorted items
    const result = {}
    for (const item of orderArray) {
      result[item.key] = item.value
    }
    return result
  }

  const sortedValue = Object.fromEntries(
    // sort all the options by alpha or count
    Object.entries(facetObject).sort((a, b) => {
      return params?.sortRemainingBy === 'alpha'
        ? a[0].split('|')[0].localeCompare(b[0]) // .split('|')[0] to get translated options
        : b[1] - a[1] || a[0].localeCompare(b[0]) // in order of count, if two value have the same count, sort them alphabetically
    })
  )

  return sortedValue
}

//convert filters to un-translated key for algolia api payload
export const decodeFiltersI18n = idsFiltersCheckedList => {
  loadSectionTranslations('plp')
  const { locale } = wcs.getStoreConfig()
  // find filter translation key to pass to Algolia api
  const filteredKeys = Object.keys(i18n.messages[locale])
    .filter(key => key.startsWith('attributes') || key.startsWith('sortBadge'))
    .reduce((acc, key) => {
      acc[key] = i18n.messages[locale][key]
      return acc
    }, {})
  const invertedTranslations = Object.fromEntries(Object.entries(filteredKeys).map(([k, v]) => [v, k]))

  idsFiltersCheckedList = idsFiltersCheckedList?.map(item => {
    if (!item?.startsWith('price')) {
      const parts = item.split(':')
      return `${invertedTranslations[parts[0]]?.split('?')[0]}:${parts[1]}`
    } else {
      return item
    }
  })
  return idsFiltersCheckedList
}

//get multiple queries result and build merged response with all proper counts and options
export const mergefiltersResult = filtersResult => {
  let [priceRangeCountQuery, allFacets, filteredAppliedFacets, ...rest] =
    filtersResult.length > 2 ? filtersResult : [filtersResult[0], filtersResult[1], [], []]
  //loop all filter group applied and use response to populate count and 0

  if (filteredAppliedFacets.nbHits > 0) {
    rest.forEach(response => {
      if (response && Object.keys(response.facets ?? {}).length) {
        const key = Object.keys(response.facets)[0]
        allFacets.facets[key] &&
          Object.keys(allFacets.facets[key])?.forEach(subKey => {
            if (!filteredAppliedFacets.facets[key]) {
              filteredAppliedFacets.facets[key] = {}
            }
            //if option is present in response assign count, otherwise set to 0:
            filteredAppliedFacets.facets[key][subKey] = Object.keys(response.facets[key]).includes(
              subKey
            )
              ? response.facets[key][subKey]
              : 0
          })
      }
    })

    //for all filters group not applied add all missing options and set count to 0
    for (const key in allFacets.facets) {
      if (!filteredAppliedFacets.facets[key]) {
        filteredAppliedFacets.facets[key] = {}
      }
      for (const subKey in allFacets.facets[key]) {
        if (!filteredAppliedFacets.facets[key][subKey]) {
          filteredAppliedFacets.facets[key][subKey] = 0
        }
      }
    }
  } else filteredAppliedFacets = allFacets

  //force insert photocromic option in lens type facet group (taken from photocromic attribute):
  if (allFacets.facets?.['attributes_translated.PHOTOCHROMIC']?.['TRUE']) {
    filteredAppliedFacets.facets['attributes_translated.LENS_TREATMENT_FACET'][
      i18n.t('attributes_translated.PHOTOCHROMIC')
    ] = filteredAppliedFacets.facets['attributes_translated.PHOTOCHROMIC']['TRUE']
  }

  //update price range facets count based on non-price filters applied
  filteredAppliedFacets.facets['sortPrice_Guest'] =
    priceRangeCountQuery.facets['sortPrice_Guest'] ||
    filteredAppliedFacets.facets['sortPrice_Guest']
  filteredAppliedFacets.facets['sortPrice_EarlyAccess'] =
    priceRangeCountQuery.facets['sortPrice_EarlyAccess'] ||
    filteredAppliedFacets.facets['sortPrice_EarlyAccess']

  return filteredAppliedFacets
}

export const getAlgoliaPlpFacets = async payload => {
  let { idsFiltersCheckedList, currentPage, plpIsExploded, currentProductsCount, searchTerm, override, pageSize } =
    payload

  const batchSize = pageSize ?? DEFAULT_PAGE_SIZE
  // `override` marks a fresh filter/sort apply (the grid is replaced, not appended).
  // Without it, a non-empty grid would be mistaken for a "Load More" and fetch an
  // offset slice — landing past the end of selective results (e.g. dash filters) and
  // returning an empty grid. Load More omits `override`, so it keeps the offset path.
  const isLoadMore = currentProductsCount > 0 && !override

  /* start translate fake hinge filter */
  let minHinge, maxHinge
  idsFiltersCheckedList.forEach(item => {
    if (item.includes('Hinge')) {
      const matchValue = item.match(/\{([^}]+)\}/)
      const valuesArray = matchValue[1].split(',').map(value => parseInt(value.trim(), 10))
      minHinge = valuesArray[0]
      maxHinge = valuesArray[1]
    }
  })
  if (idsFiltersCheckedList.length)
    idsFiltersCheckedList = idsFiltersCheckedList.filter(item => !item.includes('Hinge'))
  if (minHinge && maxHinge) {
    const hingeValue = getHingeDistanceArrayValue(minHinge, maxHinge)
    hingeValue.forEach(value => {
      idsFiltersCheckedList.push(`Hinge:${value}`)
    })
  }
  /* end translate fake hinge filter */

  const mergedArray = await formatFacetsLogic(idsFiltersCheckedList)
  const facetFilters = mergedArray
  let categoryFacet = ''

  const alphaFilters = facetFilters.filter(element => {
    return (
      !element[0]?.toLowerCase().startsWith('price') &&
      !element[0]?.toLowerCase().startsWith('sale')
    )
  })

  if (payload.currentPlpCategory && !payload.searchTerm)
    categoryFacet = `categories:${payload.currentPlpCategory}`
  const ruleContext = getSafeBrandRuleContext(
    payload.searchTerm,
    [],
    payload.currentPlpCategory === 'brands_chanel'
  )
  const mergedRuleContexts = buildRuleContexts(payload.ruleContexts, ruleContext)

  // separate orderBy from rest of payload to avoid passing it to filter query indexes
  const { orderBy, ...rest } = payload

  const filterIndex = getPlpIndex({ ...rest, plpIsExploded })
  const shared = { filters: categoryFacet, query: searchTerm, ruleContexts: mergedRuleContexts }

  // order matters: mergefiltersResult expects [priceRange, allFacets, filteredApplied, ...perFilter]
  const filterQueries = [
    buildFilterQuery(filterIndex, { ...shared, facetFilters: alphaFilters, facets: ['prices.*', 'sort*'] }),
    buildFilterQuery(filterIndex, { ...shared, facets: FULL_FACETS }),
    buildFilterQuery(filterIndex, { ...shared, facetFilters: alphaFilters, numericFilters: payload.numericFiltersSelected, facets: FULL_FACETS, attributesToRetrieve: attributesToRetrieveMainProducts }),
    ...alphaFilters.map(filter => buildFilterQuery(filterIndex, {
      ...shared,
      facetFilters: alphaFilters.filter(f => f !== filter),
      numericFilters: payload.numericFiltersSelected,
      facets: filter[0].split(':')[0],
    })),
  ]

  // Shared parameters for every product query. Filter-specific params (hitsPerPage / page / offset /
  // length) are NOT included here — they differ across the three pagination strategies below.
  const productsQueryBase = {
    filters: categoryFacet,
    facetFilters: alphaFilters,
    numericFilters: payload.numericFiltersSelected,
    type: 'default',
    facets: FULL_FACETS,
    query: searchTerm,
    ruleContexts: mergedRuleContexts,
    explain: true,
    attributesToRetrieve: attributesToRetrieveVariants,
  }

  // ─── Dynamic page-size calculation ────────────────────────────────────────────
  //
  // The PLP grid is 4 columns wide on desktop. CMS banners rendered server-side
  // each span 2 columns (CSS: grid-column: span 2), so every banner "consumes"
  // BANNER_SLOT_SIZE (= 2) product slots in the visual grid.
  //
  // To make products + banners together fill exactly DEFAULT_PAGE_SIZE (= 24) grid
  // cells on the first page, we reduce hitsPerPage by (bannerCount × BANNER_SLOT_SIZE).
  // Example with 2 banners:  24 − (2 × 2) = 20 products + 4 banner-cells = 24 cells ✓
  //
  // When filters are active, CMS banners are hidden by the back-end, so we fall
  // back to the full DEFAULT_PAGE_SIZE (no reduction needed).
  //
  // getPlpPageSize() encapsulates this logic; its result is used for page 1 only.
  // Every subsequent "load more" batch always fetches a full DEFAULT_PAGE_SIZE
  // because banners are never inserted between load-more results.
  const effectiveFirstPageSize = pageSize ?? getPlpPageSize(idsFiltersCheckedList.length > 0)

  // ─── Algolia product query strategy ───────────────────────────────────────────
  //
  // We use three distinct strategies depending on the call context.
  // The key constraint is pagination consistency: mixing page/hitsPerPage and
  // offset/length in the same browsing session would create product gaps or
  // duplicates. The approach below uses:
  //   • page + hitsPerPage  → only for the very first page-1 load
  //   • offset + length     → for every load-more call and for multi-page reloads
  //
  // This way Algolia always receives an exact byte-range request for load-more
  // and reload, independent of whatever hitsPerPage was used on page 1.
  let productQueries

  if (isLoadMore) {
    // ── Strategy A: Load More ──────────────────────────────────────────────────
    // The user clicked "Load More" while on the page. currentProductsCount holds
    // the exact number of products already rendered in the grid (e.g. 20 after
    // the banner-adjusted first page).
    //
    // Using Algolia's offset+length instead of page+hitsPerPage lets us request
    // precisely the next DEFAULT_PAGE_SIZE products starting right after the
    // already-loaded ones, regardless of the different hitsPerPage used on page 1.
    //
    // If we used page+hitsPerPage here with hitsPerPage=24 and page=1, Algolia
    // would start from product 25 (24×1), skipping products 21-24 that were
    // never fetched due to the reduced first-page size.
    productQueries = [
      buildProductQuery(getPlpIndex({ ...payload, plpIsExploded }), {
        ...productsQueryBase,
        offset: currentProductsCount,   // start right after the last loaded product
        length: batchSize,
      }),
    ]
  } else if (currentPage <= 1) {
    // ── Strategy B: Initial page-1 load ───────────────────────────────────────
    // Standard first visit or filter/sort change (store is empty, currentPage=1).
    // Use classic page+hitsPerPage so that Algolia's nbPages response is computed
    // against effectiveFirstPageSize, giving us the correct total-page count for
    // the load-more button before any load-more has occurred.
    productQueries = [
      buildProductQuery(getPlpIndex({ ...payload, plpIsExploded }), {
        ...productsQueryBase,
        hitsPerPage: effectiveFirstPageSize,
        page: 0,
      }),
    ]
  } else {
    // ── Strategy C: Hard reload with currentPage > 1 ──────────────────────────
    // The user reloaded the browser (or navigated back) while the URL contains
    // ?currentPage=N (N ≥ 2). The Vuex store is empty (currentProductsCount=0,
    // so isLoadMore is false), but we must restore ALL products that were visible
    // before the reload — i.e. the same ranges that were fetched at runtime.
    //
    // At runtime:
    //   • Page 1 was fetched with offset 0, length effectiveFirstPageSize (e.g. 20)
    //   • Each subsequent "Load More" batch used offset=<loaded so far>, length=24
    //
    // So for currentPage=2 we need:
    //   query[0]: offset=0,  length=20  → products  1-20
    //   query[1]: offset=20, length=24  → products 21-44
    //
    // For currentPage=3:
    //   query[0]: offset=0,  length=20  → products  1-20
    //   query[1]: offset=20, length=24  → products 21-44
    //   query[2]: offset=44, length=24  → products 45-68
    //
    // The multipleQueries call sends all of them in a single network round-trip;
    // the results are flatMapped into one hits array below.
    productQueries = [
      // Reconstruct page 1 range (banner-adjusted size)
      buildProductQuery(getPlpIndex({ ...payload, plpIsExploded }), {
        ...productsQueryBase,
        offset: 0,
        length: effectiveFirstPageSize,
      }),
      // Reconstruct each load-more range (pages 2 … currentPage)
      ...Array.from({ length: currentPage - 1 }, (_, i) =>
        buildProductQuery(getPlpIndex({ ...payload, plpIsExploded }), {
          ...productsQueryBase,
          offset: effectiveFirstPageSize + i * batchSize,
          length: batchSize,
        })
      ),
    ]
  }

  const mergedQueries = [...filterQueries, ...productQueries]
  const filtersAndProductsResult = await client.multipleQueries(mergedQueries)
  const filterResults = filtersAndProductsResult?.results?.filter(r => !r.hits?.length)
  const productResults = filtersAndProductsResult.results?.filter(result => result.hits?.length)
  const res = mergefiltersResult(filterResults)
  if (res.nbHits == 0) {
    const resultWithUserData = filterResults.find(r => r.userData && r.userData.length > 0)

    return {
      nbHits: 0,
      userData: resultWithUserData?.userData || []
    }
  }

  //order facets according renderingContent order in response:
  if (res.facets['attributes_translated.HINGE_DISTANCE'])
    sessionStorage.setItem(
      'hingeDistanceArray',
      Object.keys(res.facets['attributes_translated.HINGE_DISTANCE'])
    )
  const orderedFacets = {}
  if (res.renderingContent?.facetOrdering?.facets?.order) {
    // force inserting sortDiscount_Guest and sortDiscount_EarlyAccess in facets
    res.renderingContent.facetOrdering.facets.order.push('sortDiscount_Guest')
    res.renderingContent.facetOrdering.facets.order.push('sortDiscount_EarlyAccess')

    res.renderingContent.facetOrdering.facets.order.forEach(facetKey => {
      if (res.facets.hasOwnProperty(facetKey)) {
        res.facets[facetKey] = sortOptions(
          res.facets[facetKey],
          res.renderingContent.facetOrdering.values?.[facetKey]
        )
        orderedFacets[facetKey] = res.facets[facetKey]
      }
    })
    res.facets = orderedFacets
  }
  // remove gender facet if we are in gender category
  if (categoryFacet.includes('gender')) {
    delete res.facets['attributes_translated.GENDER']
  }
  // Merge hits from all product queries into a single ordered array.
  // For strategy C (multi-query reload) this flatMap concatenates the results of
  // all offset-based queries in the same order they were built above.
  const hits = productResults?.flatMap(result => result.hits)
  const objectIDs = hits.slice(-18).map(hit => hit.objectID)
  const firstProduct = productResults?.[0]
  queryIDGrouped = firstProduct?.queryID || ''
  const nbHits = firstProduct?.nbHits ?? 0

  // For load-more (strategy A) and multi-page reload (strategy C), the Algolia
  // response may not include a reliable hitsPerPage field because we used
  // offset+length instead of page+hitsPerPage. We normalise to DEFAULT_PAGE_SIZE
  // so that the load-more component always receives a consistent page size.
  const hitsPerPage = (isLoadMore || currentPage > 1) ? batchSize : firstProduct?.hitsPerPage

  // Compute total pages in a way that is consistent across all three strategies.
  //
  // Page 1 holds effectiveFirstPageSize products; every subsequent page holds
  // DEFAULT_PAGE_SIZE products. Therefore:
  //
  //   nbPages = 1  +  ceil( max(0, nbHits − effectiveFirstPageSize) / DEFAULT_PAGE_SIZE )
  //
  // Examples (effectiveFirstPageSize=20, DEFAULT_PAGE_SIZE=24):
  //   nbHits=19  → 1 + ceil(0/24)   = 1  (all products fit on page 1)
  //   nbHits=20  → 1 + ceil(0/24)   = 1  (page 1 exactly full, no load-more needed)
  //   nbHits=44  → 1 + ceil(24/24)  = 2  (page 1: 20 products, page 2: 24 products)
  //   nbHits=200 → 1 + ceil(180/24) = 9
  //
  // When filters are active, effectiveFirstPageSize equals DEFAULT_PAGE_SIZE (24),
  // so the formula reduces to the standard ceil(nbHits / 24).
  const nbPages = 1 + Math.ceil(Math.max(0, nbHits - effectiveFirstPageSize) / batchSize)
  const resultWithUserData = [firstProduct, ...filterResults].find(
    r => r?.userData && r.userData.length > 0
  )
  return {
    ...res,
    page: currentPage,
    hitsPerPage,
    nbPages,
    nbHits,
    hits,
    userData: resultWithUserData?.userData || firstProduct?.userData,
    objectIDs,
    queryID: firstProduct?.queryID,
    isSearch: firstProduct?.query !== '',
  }
}

export const getRelatedProductsMultiple = async params => {
  // Build the array of queries for Algolia Recommend API
  const algoliaQueries = params.map(({ objectID, contextName }) => ({
    indexName: ALGOLIA_CONFIG.groupedIndex,
    objectID,
    queryParameters: {
      enableRules: true,
      ...(contextName ? { ruleContexts: [contextName] } : {}),
      userToken: userToken,
    },
  }))

  // Call Algolia Recommend API with all queries at once
  const res = await clientRelated.getRelatedProducts(algoliaQueries)
  const relatedProducts = res.results.map(result => {
    return { hits: result.hits }
  })
  return relatedProducts
}

const sortedNamesIndex = {
  3: window.algoliaConfig.isEarlyAccessParam ? 'earlyaccess_asc' : 'standard_asc',
  4: window.algoliaConfig.isEarlyAccessParam ? 'earlyaccess_desc' : 'standard_desc',
  1: 'new',
}

export const setGroupedIndex = function (sortOrder) {
  if (!sortOrder || sortOrder === 'default') {
    index = client.initIndex(ALGOLIA_CONFIG.groupedIndex)
    return
  }
  index = client.initIndex(`${ALGOLIA_CONFIG.groupedIndex}__${sortedNamesIndex[sortOrder]}`)
}
export const getPlpIndex = options => {
  const { plpIsExploded, orderBy } = options || {}
  return plpIsExploded ? getUngroupedIndex(orderBy) : getGroupedIndex(orderBy)
}
export const getUngroupedIndex = function (sortOrder) {
  if (!sortOrder || sortOrder === 'default') {
    return ALGOLIA_CONFIG.ungroupedIndex
  }
  return `${ALGOLIA_CONFIG.ungroupedIndex}__${sortedNamesIndex[sortOrder]}`
}
export const getGroupedIndex = function (sortOrder) {
  if (!sortOrder || sortOrder === 'default') {
    return ALGOLIA_CONFIG.groupedIndex
  }
  return `${ALGOLIA_CONFIG.groupedIndex}__${sortedNamesIndex[sortOrder]}`
}
//format the applied filters array to satisfy AND vs OR conditions by nesting selected options of same groupname (= OR) (cfr Algolia doc)
//and convert filter label translation value to key
export const formatFacetsLogic = async (idsFiltersCheckedList = []) => {
  idsFiltersCheckedList = await decodeFiltersI18n(idsFiltersCheckedList)

  let mergedArray = []
  if (idsFiltersCheckedList?.length) {
    const groupedItems = {}
    for (const str of idsFiltersCheckedList) {
      if (!str) {
        return []
      }
      const [key, value] = str.split(':')
      const groupKey = key.trim()

      if (!groupedItems[groupKey]) {
        groupedItems[groupKey] = [str]
      } else {
        groupedItems[groupKey].push(str)
      }
    }

    for (const groupKey in groupedItems) {
      mergedArray.push(groupedItems[groupKey])
    }
    const isEarlyAccessUser = window.algoliaConfig?.isEarlyAccessParam

    const indexSale = mergedArray.findIndex(item =>
      item.some(element => element.startsWith('attributes_translated.SALE'))
    )
    if (indexSale !== -1) {
      mergedArray[indexSale] = isEarlyAccessUser
        ? [...mergedArray[indexSale], 'sortDiscount_Guest>0', 'sortDiscount_EarlyAccess>0']
        : [...mergedArray[indexSale], 'sortDiscount_Guest>0']
      mergedArray = mergedArray.map(innerList =>
        innerList.filter(item => item !== 'attributes_translated.SALE:TRUE')
      )
    }

    //add unisex if male or female are selected
    const indexGender = mergedArray.findIndex(item =>
      item.some(element => element.includes('MALE') || element.includes('FEMALE'))
    )
    if (indexGender !== -1) {
      mergedArray[indexGender] = [...mergedArray[indexGender], `attributes_translated.GENDER:${i18n.t('plp_gender_unisex')}`]
    }

    //add photocromic true if lens treatment photocromic option is selected:
    const indexPhotocromic = mergedArray.findIndex(item =>
      item.some(element => element.includes(i18n.t('attributes_translated.PHOTOCHROMIC')))
    )
    if (indexPhotocromic !== -1) {
      mergedArray[indexPhotocromic] = [...mergedArray[indexPhotocromic], 'attributes_translated.PHOTOCHROMIC:TRUE']
    }

    return mergedArray
  }
  return mergedArray
}

export const algoliaAnalyticsEvents = params => {
  if (params.eventType === 'Click' && params.eventName == 'PDP Grouped') {
    params.index = ALGOLIA_CONFIG.groupedIndex
    params.queryId = queryIDGrouped
  } else if (
    params.eventType === 'variantHover' ||
    (params.eventType === 'Click' && params.eventName == 'PDP Ungrouped')
  ) {
    params.index = ALGOLIA_CONFIG.ungroupedIndex
    const objectID = params.objectIDs[0]
    params.queryId = objectIDsMapUnGrouped.find(item => item.objectID === objectID)?.queryID ?? ''
  }
  const eventParams = {
    objectIDs: params.objectIDs,
    index: params.index,
    userToken: userToken,
    eventName: params.eventName,
    clickAnalytics: true,
  }

  if (!isEmpty(params.queryId)) eventParams.queryID = params.queryId

  if (params.eventAlgolia !== 'viewedObjectIDs' && params.eventAlgolia !== 'clickedObjectIDs') {
    eventParams.positions = [params.position || 0]
  }

  aa(params.eventAlgolia, eventParams)
}
export const tealiumAnalyticsEvents = params => {
  const objectID = params.objectID[0]
  const mainProductObjectID = params.mainProduct
  const matchedUnGrouped = objectIDsMapUnGrouped.find(item => item.objectID === objectID)
  const matchedGrouped = objectIDsMapPositionGrouped.find(item => item.objectID === mainProductObjectID)
  const colorPosition = matchedUnGrouped?.position ?? ''

  const analyticsParams = {
    id: 'Click',
    eventName: isSearch ? 'Search – Go to product' : 'PLP – Go to product',
    data_element_id: params.dataElementId ?? '',
    data_description: params.dataDescription ?? '',
    objectID: objectID,
    grouped_queryID: queryIDGrouped,
    ungrouped_queryID: matchedUnGrouped?.queryID ?? '',
    grouped_indexName: ALGOLIA_CONFIG.groupedIndex,
    ungrouped_indexName: ALGOLIA_CONFIG.ungroupedIndex,
    grouped_position: matchedGrouped?.index ?? '',
    ungrouped_position: colorPosition,
    userToken: userToken,
  }

  sessionAlgoliaParams(analyticsParams)

  window.tealium_data2track.push(analyticsParams)
}

export const algoliaPurchaseEvent = () => {
  const savedCartState = JSON.parse(localStorage.getItem('savedCartStateForAlgolia')) || []
  const savedCartMap = new Map(savedCartState.map(item => [item.orderItemId, item.algoliaSession]))

  const currency = window.purchaseRows.length ? window.purchaseRows[0].currency : ''

  const productIds = window.purchaseRows.map(item => item.productId)

  const generateProductData = index => {
    return window.purchaseRows.map(({ unitPrice, quantity, orderItemId, productId }) => {
      const newProduct = { price: unitPrice, quantity: Number(quantity) }

      if (savedCartMap.has(orderItemId)) {
        const session = savedCartMap.get(orderItemId)
        if (index === ALGOLIA_CONFIG.groupedIndex)
          newProduct.queryID = session.queryIDAlgolia_grouped
        else {
          const ungroupedData = session.queryIDAlgolia_ungrouped
            ? JSON.parse(session.queryIDAlgolia_ungrouped)
            : []
          newProduct.queryID =
            ungroupedData.find(item => item.objectID === productId)?.queryID || null
        }
      }

      return newProduct
    })
  }

  const baseEvent = {
    eventName: 'Products Purchased',
    eventType: 'conversion',
    eventSubtype: 'purchase',
    objectIDs: productIds,
    userToken: userToken,
    currency: currency,
    value: window.purchaseTotal,
  }

  const indices = [ALGOLIA_CONFIG.groupedIndex, ALGOLIA_CONFIG.ungroupedIndex]
  const events = indices.map(index => ({
    ...baseEvent,
    objectData: generateProductData(index),
    index,
  }))

  aa('sendEvents', events)
}

function sessionAlgoliaParams(params) {
  if (!queryIDGrouped && !objectIDsMapUnGrouped.length)
    sessionStorage.removeItem('sessionDataAlgolia')
  else {
    // set session storage for add to bag and pdp landing page
    const sessionDataAlgolia = {
      queryIDAlgolia_grouped: queryIDGrouped,
      queryIDAlgolia_ungrouped: JSON.stringify(objectIDsMapUnGrouped),
      indexNameAlgolia_grouped: ALGOLIA_CONFIG.groupedIndex,
      indexNameAlgolia_ungrouped: ALGOLIA_CONFIG.ungroupedIndex,
      objectdIDsAlgolia_grouped: params.objectID,
      positionAlgolia_grouped: params.grouped_position,
      positionAlgolia_ungrouped: params.ungrouped_position,
      userToken: userToken,
    }
    sessionStorage.setItem('sessionDataAlgolia', JSON.stringify(sessionDataAlgolia))
  }
}

function getHingeDistanceArrayValue(min, max) {
  const arrayOfStrings = sessionStorage.getItem('hingeDistanceArray').split(',')
  const arrayOfNumbers = arrayOfStrings.map(str => parseFloat(str))
  const extractedNumbers = arrayOfNumbers.filter(num => num >= min && num <= max)

  return extractedNumbers
}

export const algoliaGetRule = async (rule, maxProduct, indexType = 'grouped') => {
  const indexName = indexType === 'ungrouped'
    ? ALGOLIA_CONFIG.ungroupedIndex
    : ALGOLIA_CONFIG.groupedIndex
  const res = {
    hits: [],
    userData: [],
    objectIDs: [],
    indexName,
    queryID: '',
  }
  try {
    const result = await index.search('', {
      hitsPerPage: maxProduct,
      ruleContexts: [rule],
      userToken: userToken,
      clickAnalytics: true,
    })
    res.hits = result.hits ?? []
    res.objectIDs = res.hits.map(hit => hit.objectID)
    res.userData = result.userData ?? []
    res.queryID = result.queryID
  } catch (error) {
    console.error(error)
  }
  return res
}
export const sendViewedObjectIDsEvent = ({ objectIDs, indexName, eventName, queryID }) => {
  if (!objectIDs || objectIDs.length === 0) {
    console.warn('sendViewedObjectIDsEvent: objectIDs is empty or undefined')
    return
  }

  const analyticsParams = {
    eventAlgolia: 'viewedObjectIDs',
    queryId: queryID,
    objectIDs: objectIDs,
    index: indexName,
    eventName: eventName,
    userToken: userToken,
  }

  algoliaAnalyticsEvents(analyticsParams)
}
export const algoliaGetTrendingFacets = async () => {
  const res = { hits: [] }
  try {
    const result = await clientRelated.getTrendingFacets(
      [
        {
          indexName: ALGOLIA_CONFIG.groupedIndex,
          facetName: 'attributes.BRAND',
          maxRecommendations: 10,
        },
      ],
      {
        queryParameters: {
          userToken: userToken,
        },
      }
    )
    res.hits = result.results[0].hits
  } catch (error) {
    console.error(error)
  }
  return res
}

export const algoliaGetTrendingItems = async (maxProduct = 20, extraFilters = '') => {
  const res = {
    hits: [],
    objectIDs: [],
    indexName: ALGOLIA_CONFIG.groupedIndex,
  }
  const baseFilters = "display:true AND buyable:true AND (lifecycle:true OR attributes.CHANNEL_DETAILS:'ONLINE ONLY' OR NOT inventoryQuantity:0) AND (NOT attributes_translated.EXCLUDE_FROM_SEARCH:TRUE) AND (NOT attributes_translated.EXCLUDE_FROM_SEARCH:TRUE|FALSE)"
  const filters = extraFilters ? `${baseFilters} AND ${extraFilters}` : baseFilters

  try {
    const result = await clientRelated.getTrendingItems([
      {
        indexName: ALGOLIA_CONFIG.groupedIndex,
        maxRecommendations: maxProduct,
        queryParameters: {
          filters: filters,
          numericFilters: ['inventoryQuantity > 0'],
          ruleContexts: ['SAFEBRANDSEARCH'],
          userToken: userToken,
        },
      },
    ])
    res.hits = result.results[0].hits
    res.objectIDs = res.hits.map(hit => hit.objectID)
  } catch (error) {
    console.error(error)
  }
  return res
}

export const algoliaGetTrendingItemsContent = async () => {
  const res = { hits: [] }
  try {
    const result = await clientRelated.getTrendingItems([
      {
        indexName: ALGOLIA_CONFIG.contentIndex,
        queryParameters: {
          ruleContexts: ['SAFEBRANDSEARCH'],
          userToken: userToken,
        },
      },
    ])
    res.hits = result.results[0].hits
  } catch (error) {
    console.error(error)
  }
  return res
}

//algolia instant search suggestions grouped and query suggestion
export const algoliaSuggestionsResults = async (searchTerm, page, facetsSelected, startPage) => {
  //build options and params for grouped product search
  const { idsFiltersCheckedList, orderBy } = facetsSelected || {}
  const mergedArray = await formatFacetsLogic(idsFiltersCheckedList)
  const facetFilters = mergedArray

  const numericFiltersSelected = buildNumericFilters(idsFiltersCheckedList)

  const filteredArray = facetFilters.filter(
    element => !element[0]?.toLowerCase().startsWith('price')
  )

  const ruleContext = getSafeBrandRuleContext(searchTerm)

  const options = {
    query: searchTerm,
    hitsPerPage: 24,
    page: page,
    attributesToRetrieve: ['*'],
    facetFilters: filteredArray,
    numericFilters: numericFiltersSelected,
    removeStopWords: true,
    ruleContexts: ruleContext,
    orderBy,
    userToken: userToken,
  }

  const addParams = {
    startPage: startPage, //todo pass dynamic start page as done for plp
  }

  const products = await algoliaPlpGroupedSearch({ options, addParams })
  const userData = products?.userData || null
  const redirect = products?.renderingContent?.redirect || null
  //retrieve and merge variants for all products
  let productsAndVariants
  if (products?.hits?.length) {
    productsAndVariants = await getProductsVariants({ productsData: products, options })
  }
  //build search index for query suggestions and content suggestions multiple queries
  const searchIndex = [
    {
      indexName: `${ALGOLIA_CONFIG.groupedIndex}_query_suggestions`,
      params: {
        query: searchTerm,
        hitsPerPage: 10,
        ruleContexts: [ruleContext].filter(Boolean),
        userToken: userToken,
      },
    },
    {
      indexName: ALGOLIA_CONFIG.contentIndex,
      params: {
        query: searchTerm,
        attributesToRetrieve: ['title', 'url'],
        attributesToHighlight: ['title'],
        attributesToSnippet: ['title'],
        hitsPerPage: 6,
      },
    },
  ]

  const otherSuggestions = await client.multipleQueries(searchIndex)

  return {
    productsFinal: productsAndVariants,
    suggestedWords: otherSuggestions.results[0],
    contentResult: otherSuggestions.results[1],
    userData: userData,
    redirect: redirect,
    explain: products.explain || null,
  }
}

//algolia instant search content suggestions
export const algoliaSuggestionsContent = async (searchTerm, page = 0) => {
  const contentIndex = [
    {
      indexName: ALGOLIA_CONFIG.contentIndex,
      params: {
        query: searchTerm,
        hitsPerPage: 6,
        page: page,
        userToken: userToken,
      },
    },
  ]
  return await client.multipleQueries(contentIndex)
}

//algolia simple ungrouped call for carousels
export const algoliaSimpleUnGrouped = async products => {
  const { hits } = products
  const queries = hits.map(hit => ({
    hitsPerPage: 20,
    ruleContexts: ['SAFEBRANDSEARCH'],
    indexName: ALGOLIA_CONFIG.ungroupedIndex,
    facetFilters: [`x_groupkey: ${hit.x_groupkey}`],
    numericFilters: [[]],
    query: '',
    userToken: userToken,
  }))
  return await client.multipleQueries(queries)
}

/**
 * algoliaMultipleRules
 * @description: get multiple rules from algolia
 * @params {rules} array of rules
 * @return {Promise} resolves to the results of multiple queries
 **/
export const algoliaMultipleRules = async (rules = []) => {
  const queries = (rules ?? []).map(rule => ({
    indexName: rule.indexName,
    params: {
      hitsPerPage: rule.hitsNumber,
      ruleContexts: [rule.name],
    },
    userToken: userToken,
  }))
  return client.multipleQueries(queries)
}


/**
 * algoliaSingleRule
 * @description: get a single rule from algolia
 * @params {rules} array of rules
 * @return {Promise<Readonly<Promise<MultipleQueriesResponse<?>>>> | Readonly<Promise<MultipleQueriesResponse<?>>>} resolves to the results of multiple queries
 **/
export const algoliaSingleRule = async (rule = {}) => {
  const queries = [{
    indexName: rule.indexName,
    params: {
      hitsPerPage: rule.hitsNumber,
      ruleContexts: [rule.name],
      filters: rule.filters || '',
    },
    userToken: userToken,
  }]

  return client.multipleQueries(queries)
}

/**
 * getSearchContentAndSuggestions
 * @description: fetches search content and suggestions from Algolia based on the provided search term.
 * @param searchTerm
 * @returns {Promise<Readonly<Promise<MultipleQueriesResponse<?>>>> | Readonly<Promise<MultipleQueriesResponse<?>>>}
 */
export const getSearchContentAndSuggestions = async searchTerm => {
  //build search index for query suggestions and content suggestions multiple queries
  try {
    const ruleContext = getSafeBrandRuleContext(searchTerm)
    const searchIndex = [
      {
        indexName: `${ALGOLIA_CONFIG.groupedIndex}_query_suggestions`,
        params: {
          query: searchTerm,
          hitsPerPage: 10,
          ruleContexts: [ruleContext].filter(Boolean),
          userToken: userToken,
        },
      },
      {
        indexName: ALGOLIA_CONFIG.contentIndex,
        params: {
          query: searchTerm,
          attributesToRetrieve: ['title', 'url'],
          attributesToHighlight: ['title'],
          attributesToSnippet: ['title'],
          hitsPerPage: 6,
        },
      },
    ]

    const suggestions = await client.multipleQueries(searchIndex)
    return {
      suggestedWords: suggestions.results[0],
      contentResult: suggestions.results[1],
    }
  } catch (error) {
    console.error('Error fetching search content and suggestions:', error)
    throw error
  }
}

export const getProductsVariantsOptions = async (searchTerm, page, facetsSelected) => {
  const { idsFiltersCheckedList, orderBy } = facetsSelected || {}
  const facetFilters = await formatFacetsLogic(idsFiltersCheckedList)
  const numericFiltersSelected = buildNumericFilters(idsFiltersCheckedList)
  let filteredArray = []
  if (facetFilters) {
    filteredArray = facetFilters.filter(element => !element[0]?.toLowerCase().startsWith('price'))
  }
  return {
    query: searchTerm,
    hitsPerPage: 24,
    page: page,
    attributesToRetrieve: ['*'],
    facetFilters: filteredArray,
    numericFilters: numericFiltersSelected,
    removeStopWords: true,
    ruleContexts: getSafeBrandRuleContext(searchTerm),
    orderBy,
    userToken: userToken,
  }
}

/**
 * algoliaGetRuleWithVariants
 * @description: get rule from algolia and merge variants for grouped index
 * @params {string} rule - the context rule
 * @params {number} maxProduct - limit of hits
 * @params {string} indexType - grouped or ungrouped
 **/
export const algoliaGetRuleWithVariants = async (rule, maxProduct, indexType = 'grouped') => {
  const res = await algoliaGetRule(rule, maxProduct, indexType)

  if ((indexType === 'grouped' || !indexType) && res.hits && res.hits.length > 0) {
    const options = {
      query: '',
      facetFilters: [],
      numericFilters: [],
      ruleContexts: rule,
    }

    let productsData = { hits: res.hits }

    try {
      const variants = await algoliaPlpUngroupedSearch({
        products: productsData,
        options
      })

      productsData = mergeProdsAndVariants({
        products: productsData,
        variants
      }, false)

      res.hits = productsData.hits
    } catch (error) {
      console.error('Error fetching variants for rule:', error)
    }
  }

  return res
}

